// LB-346 (Alan, 19 Sep / 29 Sep): the child's own view of `state.requests`.
//
// Before this a refused earn request simply vanished from the kid side — the only refusal a
// child ever saw was a turned-down vault transfer, via Recent activity. The rule of record
// from LB-312: the child learns EVERY outcome, a yes is named ("Grandma said yes") and a no
// is NEVER attributed to an adult ("Not this time", no name). Parent history still records
// who decided; taking the name off here is a rendering rule, not an audit gap.
//
// Pure: no React, no state writes. ChildView's RecentActivityCard renders what this returns
// (Alan, 30 Sep, option R: ONE list on Kid Home — waiting rows pinned above the timeline,
// refusals in the timeline where they happened, approved rows untouched because a named yes
// is already there as a transaction, LB-343) and scripts/check-kid-requests.mjs asserts it.
import { attributionName } from "./adults.js";

export const KID_REQUEST_DAYS = 30; // answered rows shown for this long (Alan, 29 Sep: "Fine")

// A shared list from another household is the parent's decision about someone else's words,
// not something the child asked for, so it never appears here.
const HIDDEN_TYPES = new Set(["homework_shared"]);

// The only pending request a child can withdraw today is a vault transfer (VaultTransferCard's
// Cancel → App.cancelRequest). The list offers the same act on the same rows and nothing more.
const WITHDRAWABLE = new Set(["vault_transfer"]);

function when(r) { return r.resolvedAt || r.timestamp || ""; }

function stateOf(r) {
  if (r.status === "pending") return "waiting";
  if (r.status === "approved") return "yes";
  if (r.status === "rejected") return "no";
  if (r.status === "goal") return "goal"; // an ask the child turned into a goal (LB-17)
  return "back"; // cancelled: the child took it back
}

// One row per request, in the shape the card draws. `adults` is the household list (for a
// name on a yes that carries only an adultId); `now` is injectable for the harness.
export function kidRequestRow(r, adults = [], now = Date.now()) {
  const state = stateOf(r);
  // A yes is named only when the record names someone. There is deliberately no "A grown-up
  // said yes" — it tells a child nothing (same rule as kidHistoryLead in ChildView).
  const name = state === "yes" ? attributionName({ adults }, r) : "";
  const note = state === "no" ? String(r.parentNote || "").trim() : "";
  const tokens = Number(r.requestedAmount) || 0;
  return {
    id: r.id,
    requestType: r.requestType,
    title: String(r.title || "").trim(),
    tokens,
    pricePence: r.requestType === "ask_to_buy" && Number.isFinite(r.pricePence) ? r.pricePence : null,
    at: state === "waiting" ? (r.timestamp || "") : when(r),
    state,
    name,
    note,
    canWithdraw: state === "waiting" && WITHDRAWABLE.has(r.requestType),
    _now: now,
  };
}

// { waiting: newest first, answered: newest first within the last KID_REQUEST_DAYS }.
export function kidRequestRows(requests, adults = [], now = Date.now()) {
  const cutoff = now - KID_REQUEST_DAYS * 24 * 60 * 60 * 1000;
  const waiting = [], answered = [];
  for (const r of requests || []) {
    if (!r || HIDDEN_TYPES.has(r.requestType)) continue;
    const row = kidRequestRow(r, adults, now);
    if (row.state === "waiting") { waiting.push(row); continue; }
    const t = new Date(row.at).getTime();
    if (Number.isFinite(t) && t < cutoff) continue;
    answered.push(row);
  }
  const byTime = (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime();
  waiting.sort(byTime); answered.sort(byTime);
  return { waiting, answered };
}

// The one list (option R, 30 Sep). `waiting` is pinned above the timeline and is NOT in date
// order with it; `timeline` is transactions and the requests that never became one (refused,
// taken back, made a goal), newest first, requests limited to KID_REQUEST_DAYS. An approved
// request is left out: its transaction is already in the timeline, named (LB-343).
export function kidTimeline(transactions, requests, adults = [], now = Date.now()) {
  const { waiting, answered } = kidRequestRows(requests, adults, now);
  const items = [];
  for (const tx of transactions || []) if (tx) items.push({ kind: "tx", id: `tx:${tx.id}`, at: tx.timestamp || "", tx });
  for (const row of answered) if (row.state !== "yes") items.push({ kind: "req", id: `req:${row.id}`, at: row.at, row });
  items.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  return { waiting, timeline: items };
}
